import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import { loadRazorpayScript, createOrder, verifyPayment, openCheckout, checkOrderStatus } from '../services/razorpayService';
import usePricingPageTracking from '../hooks/usePricingPageTracking';
import emailService from '../services/emailService';
import { useUser } from '../contexts/UserContext';
import './PricingPage.css';

// Plan hierarchy for upgrade/downgrade checks
const PLAN_HIERARCHY = { free: 0, silver: 1, gold: 2, platinum: 3 };

function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUser();
  const [selectedPlan, setSelectedPlan] = useState(() => {
    // For active paid plan users, don't pre-select any plan (all cards same size)
    // They select a plan only when they want to upgrade
    return 'gold';
  });

  // Reset selectedPlan to null for active paid plan users so no card is pre-enlarged
  useEffect(() => {
    if (userData?.planType && userData.planType !== 'free' && userData?.planExpiryDate) {
      const expiry = userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate);
      if (expiry > new Date()) {
        setSelectedPlan(null);
      }
    }
  }, [userData]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [plansData, setPlansData] = useState({});
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discountOffer }

  // Track pricing page visits for abandoned cart emails
  usePricingPageTracking();

  // Track whether user completed a purchase (prevents abandonment email)
  const purchaseCompletedRef = useRef(false);
  const pageEntryTimeRef = useRef(Date.now());

  // Send payment-abandonment email when user leaves pricing page without buying
  useEffect(() => {
    return () => {
      const user = auth.currentUser;
      const timeSpentSeconds = Math.floor((Date.now() - pageEntryTimeRef.current) / 1000);
      if (!purchaseCompletedRef.current && user && timeSpentSeconds >= 5) {
        emailService.sendPaymentAbandonmentEmail(user.uid, user.email, user.displayName || '');
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to format duration from daysRange
  const formatDuration = (daysRange) => {
    if (!daysRange) return 'N/A';
    
    if (daysRange === 1) return '1 Day Access';
    if (daysRange <= 7) return `${daysRange} Days Access`;
    if (daysRange <= 30) return `${daysRange} Days Access`;
    if (daysRange >= 365) {
      const years = Math.floor(daysRange / 365);
      const extraDays = daysRange % 365;
      const months = Math.floor(extraDays / 30);
      
      if (months > 0) {
        return `${years} Year${years > 1 ? 's' : ''} + ${months} Month${months > 1 ? 's' : ''} Access`;
      }
      return `${years} Year${years > 1 ? 's' : ''} Access`;
    }
    
    const months = Math.floor(daysRange / 30);
    return `${months} Month${months > 1 ? 's' : ''} Access`;
  };

  // Helper function to format download limit text
  const formatDownloadLimit = (resumeLimit, coverLetterLimit) => {
    if (resumeLimit === -1 || coverLetterLimit === -1) {
      return 'Unlimited Downloads (All Formats)';
    }
    if (resumeLimit === coverLetterLimit) {
      return `${resumeLimit} Resume & Cover Letter Downloads`;
    }
    return `${resumeLimit} Resume Downloads`;
  };

  // Fetch complete plan data from Firestore
  useEffect(() => {
    const fetchPlansData = async () => {
      try {
        setLoadingPlans(true);
        const plansCollection = collection(db, 'plans');
        const plansSnapshot = await getDocs(plansCollection);
        
        const plans = {};
        plansSnapshot.forEach((doc) => {
          const data = doc.data();
          plans[doc.id] = {
            planType: data.planType || doc.id,
            initialPrice: data.initialPrice || data.initial_price || null,
            actualPrice: data.actualPrice || data.actual_price || null,
            currency: data.currency || 'INR',
            daysRange: data.daysRange || 0,
            resumeDownloadLimit: data.resumeDownloadLimit ?? 0,
            coverLetterDownloadLimit: data.coverLetterDownloadLimit ?? 0,
            hasAICoaching: data.hasAICoaching || false,
            hasHRConsultancy: data.hasHRConsultancy || false,
            hasMockInterview: data.hasMockInterview || false,
            hasUnlimitedStorage: data.hasUnlimitedStorage || false,
          };
        });
        
        setPlansData(plans);
      } catch (error) {
        console.error('❌ Error fetching plan data:', error);
        // Keep default hardcoded values if Firestore fetch fails
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlansData();
  }, []);

  // Build plans array dynamically from Firestore data
  const plans = [
    {
      id: 'silver',
      name: 'Silver',
      price: plansData.silver?.actualPrice || '44.99',
      originalPrice: plansData.silver?.initialPrice || '49.99',
      duration: plansData.silver ? formatDuration(plansData.silver.daysRange) : '1 Day Access',
      popular: false,
      gradient: 'linear-gradient(135deg, #B8B8B8 0%, #E8E8E8 100%)',
      features: [
        { text: 'ATS-Friendly Resume Creation', included: true },
        { text: 'Free ATS Score & Cover Letter', included: true },
        { 
          text: plansData.silver ? formatDownloadLimit(plansData.silver.resumeDownloadLimit, plansData.silver.coverLetterDownloadLimit) : '2 Downloads Per Plan', 
          included: true 
        },
        { text: 'Limited Template Access', included: true },
        { text: 'Download (PDF, Word)', included: true },
        { text: 'Extra Downloads Add-On', included: true },
        { 
          text: plansData.silver?.hasUnlimitedStorage ? 'Free Cloud Storage' : 'Limited Storage Access', 
          included: true 
        },
        { text: 'Detailed ATS Analysis', included: false },
        { text: 'AI Career Toolkit', included: plansData.silver?.hasAICoaching || false },
        { text: 'AI Career Map', included: false },
        { text: 'MCQ Test', included: plansData.silver?.hasMockInterview || false },
      ],
    },
    {
      id: 'gold',
      name: 'Gold',
      price: plansData.gold?.actualPrice || '129.99',
      originalPrice: plansData.gold?.initialPrice || '149.99',
      duration: plansData.gold ? formatDuration(plansData.gold.daysRange) : '7 Days Access',
      popular: true,
      gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      features: [
        { text: 'ATS-Friendly Resume Creation', included: true },
        { text: 'Detailed ATS Score & AI Analysis', included: true },
        { text: 'Free Cover Letter Preparation', included: true },
        { 
          text: plansData.gold ? formatDownloadLimit(plansData.gold.resumeDownloadLimit, plansData.gold.coverLetterDownloadLimit) : '30 Resume & Cover Letter Downloads', 
          included: true 
        },
        { text: 'Premium Template Access', included: true },
        { text: 'Download (PDF, Word)', included: true },
        { text: 'Discount on Promo Codes', included: true },
        { 
          text: plansData.gold?.hasUnlimitedStorage ? 'Free Cloud Storage' : 'Limited Storage Access', 
          included: true 
        },
        { text: 'Refund Applicable*', included: true },
        { text: 'AI Career Toolkit + Courses', included: true },
        { text: 'MCQ Test', included: plansData.gold?.hasMockInterview || false },
        { text: 'Unlimited Downloads', included: plansData.gold?.resumeDownloadLimit === -1 || false },
        { text: 'AI Career Map', included: false },
      ],
    },
    {
      id: 'platinum',
      name: 'Platinum',
      price: plansData.platinum?.actualPrice || '849.99',
      originalPrice: plansData.platinum?.initialPrice || '949.99',
      duration: plansData.platinum ? formatDuration(plansData.platinum.daysRange) : '12+1 Months Free',
      popular: false,
      gradient: 'linear-gradient(135deg, #E5E4E2 0%, #BCC6CC 100%)',
      features: [
        { text: 'ATS-Friendly Resume Creation', included: true },
        { 
          text: plansData.platinum?.resumeDownloadLimit === -1 ? 'Unlimited Downloads (All Formats)' : formatDownloadLimit(plansData.platinum?.resumeDownloadLimit || 0, plansData.platinum?.coverLetterDownloadLimit || 0), 
          included: true 
        },
        { text: 'Unlimited Premium Templates', included: true },
        { text: 'Unlimited Cover Letter Prep', included: true },
        { text: 'Free Detailed ATS Score Check', included: true },
        { text: 'AI Career Toolkit + Certifications', included: plansData.platinum?.hasAICoaching || false },
        { text: 'AI Career Map', included: plansData.platinum?.hasAICoaching || false },
        { text: 'MCQ Test', included: plansData.platinum?.hasMockInterview || false },
        { text: 'Discount on Promo Codes', included: true },
        { 
          text: plansData.platinum?.hasUnlimitedStorage ? 'Free Cloud Storage' : 'Limited Storage Access', 
          included: true 
        },
        { text: 'Refund Applicable*', included: true },
      ],
    },
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    // If a promo is applied, check if the new plan is eligible
    if (appliedPromo && appliedPromo.planAccepted && appliedPromo.planAccepted.length > 0) {
      if (!appliedPromo.planAccepted.includes(planId)) {
        const allowedPlans = appliedPromo.planAccepted.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
        customToast.warning(`Promo code "${appliedPromo.code}" is only valid for ${allowedPlans}. Discount removed.`);
        setAppliedPromo(null);
        setPromoCode('');
      }
    }
  };

  // Promo code handler
  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) {
      customToast.warning('Please enter a promo code');
      return;
    }

    if (!selectedPlan) {
      customToast.warning('Please select a plan first');
      return;
    }

    setPromoApplying(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        customToast.error('Please log in to apply a promo code.');
        return;
      }

      // Fetch all promo codes and match case-insensitively
      const promoSnapshot = await getDocs(collection(db, 'promoCodes'));
      const matchedDoc = promoSnapshot.docs.find(
        promoDoc => promoDoc.data().promoCode?.toUpperCase() === code.toUpperCase()
      );

      if (!matchedDoc) {
        customToast.error('Invalid promo code. Please check and try again.');
        setAppliedPromo(null);
      } else {
        const promoData = matchedDoc.data();
        const discount = parseFloat(promoData.discountOffer) || 0;

        // ── Check if this user has already used this promo code ──
        const usedByUsers = promoData.promoCodeAppliedUsers || [];
        if (usedByUsers.includes(user.uid)) {
          customToast.error('This promo code has already been used by your account.');
          setAppliedPromo(null);
          return;
        }

        // Check if selected plan is eligible for this promo code
        const planAccepted = promoData.planAccepted || [];
        if (planAccepted.length > 0 && !planAccepted.includes(selectedPlan)) {
          const allowedPlans = planAccepted.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
          customToast.error(`This promo code is only valid for ${allowedPlans} plan${planAccepted.length > 1 ? 's' : ''}.`);
          setAppliedPromo(null);
        } else {
          const discType = (promoData.discountType || 'percentage').toLowerCase();
          if (discType === 'percentage' && (discount <= 0 || discount > 100)) {
            customToast.error('This promo code is not valid.');
            setAppliedPromo(null);
          } else if (discType === 'flat' && discount <= 0) {
            customToast.error('This promo code is not valid.');
            setAppliedPromo(null);
          } else {
            setAppliedPromo({ code, discountOffer: discount, discountType: discType, planAccepted, docId: matchedDoc.id });
            customToast.success(discType === 'flat' ? `Promo code applied! ₹${discount} off.` : `Promo code applied! You get ${discount}% off.`);
          }
        }
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      customToast.error('Failed to validate promo code. Please try again.');
    } finally {
      setPromoApplying(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    customToast.info('Promo code removed');
  };

  // Calculate discounted price for the selected plan
  const getDiscountedPrice = () => {
    if (!selectedPlan || !appliedPromo) return null;
    // Check plan eligibility
    if (appliedPromo.planAccepted && appliedPromo.planAccepted.length > 0 && !appliedPromo.planAccepted.includes(selectedPlan)) {
      return null;
    }
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return null;
    const originalPrice = parseFloat(plan.price);

    let discountAmount;
    if (appliedPromo.discountType === 'flat') {
      // Flat discount: subtract fixed amount
      discountAmount = Math.min(appliedPromo.discountOffer, originalPrice);
    } else {
      // Percentage discount (default)
      discountAmount = (originalPrice * appliedPromo.discountOffer) / 100;
    }

    const finalPrice = Math.max(0, originalPrice - discountAmount).toFixed(2);
    return {
      originalPrice: plan.price,
      finalPrice,
      discountAmount: discountAmount.toFixed(2),
      discountPercent: appliedPromo.discountType === 'flat' ? null : appliedPromo.discountOffer,
      discountType: appliedPromo.discountType || 'percentage'
    };
  };

  // Razorpay Payment Handler
  const handleProceedToPayment = async () => {
    if (!selectedPlan) {
      customToast.warning('Please select a plan to continue');
      return;
    }

    // Check if user is logged in
    const user = auth.currentUser;
    if (!user) {
      customToast.warning('Please log in to purchase a plan');
      navigate('/profile');
      return;
    }

    // Downgrade protection: prevent users from purchasing a lower plan
    const currentPlan = (userData?.planType || 'free').toLowerCase();
    const isCurrentPlanActive = userData?.planExpiryDate
      ? (userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate)) > new Date()
      : false;

    if (isCurrentPlanActive && currentPlan !== 'free') {
      const currentLevel = PLAN_HIERARCHY[currentPlan] || 0;
      const selectedLevel = PLAN_HIERARCHY[selectedPlan] || 0;

      if (selectedLevel <= currentLevel) {
        const currentName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);
        if (selectedLevel === currentLevel) {
          customToast.warning(`You already have an active ${currentName} plan.`);
        } else {
          customToast.error(`You can't downgrade from ${currentName} to a lower plan. Your current plan is still active.`);
        }
        return;
      }
    }

    const plan = plans.find(p => p.id === selectedPlan);
    const priceInfo = getDiscountedPrice();
    const finalPrice = priceInfo ? parseFloat(priceInfo.finalPrice) : parseFloat(plan.price);
    const amountInPaise = Math.round(finalPrice * 100); // Razorpay expects paise

    setIsProcessing(true);

    try {
      // Step 1: Load Razorpay checkout script
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error('Failed to load payment gateway. Please check your internet connection.');
      }

      // Step 2: Create order on backend
      const orderData = await createOrder({
        planType: selectedPlan,
        amount: amountInPaise,
        userId: user.uid
      });

      // Step 3: Open Razorpay Checkout modal
      const paymentResponse = await openCheckout({
        orderId: orderData.orderId,
        amount: orderData.amount,
        currency: orderData.currency,
        keyId: orderData.keyId,
        userEmail: user.email,
        userName: user.displayName || '',
        planName: plan.name
      });

      // Step 4: Verify payment on backend & activate plan
      const verifyResult = await verifyPayment({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        userId: user.uid,
        planType: selectedPlan,
        promoCode: appliedPromo?.code || null
      });

      // Step 5: Mark purchase as completed (prevents abandoned cart email)
      await emailService.markPurchaseCompleted(user.uid, selectedPlan);
      purchaseCompletedRef.current = true;

      // Step 6: Show success & redirect
      customToast.success(`🎉 Payment successful! ${plan.name} plan activated.`);

      const returnAction = location.state?.returnAction;
      const returnUrl = location.state?.returnUrl || '/templates';
      const draftSavedToMyFiles = location.state?.draftSavedToMyFiles || false;
      const resumeId = location.state?.resumeId || null;

      // For download-related returns, go back to the editor page so user can download immediately
      if (returnAction === 'download' || returnAction === 'download-cover-letter') {
        navigate(returnUrl, {
          state: {
            message: `🎉 ${plan.name} plan activated! You can now download your ${returnAction === 'download-cover-letter' ? 'cover letter' : 'resume'}.`,
            showSuccess: true,
            returnAction,
            triggerDownload: true,
            draftSavedToMyFiles,
            resumeId
          }
        });
      } else {
        navigate(returnUrl, {
          state: {
            message: `Successfully upgraded to ${plan.name} plan!`,
            showSuccess: true
          }
        });
      }
    } catch (error) {
      console.error('❌ Payment error:', error);

      // If user explicitly cancelled (dismissed modal without paying), no need to check server
      if (error.message === 'Payment cancelled by user') {
        // Still check server-side in case money was deducted before modal dismiss
        if (error.orderId) {
          console.log('🔍 User cancelled — verifying order status server-side...');
          try {
            const statusResult = await checkOrderStatus({
              orderId: error.orderId,
              userId: user.uid,
              planType: selectedPlan
            });
            if (statusResult.success && statusResult.status === 'paid') {
              console.log('✅ Payment was actually captured! Recovering...');
              customToast.success(`🎉 Payment successful! ${plan.name} plan activated.`);
              await emailService.markPurchaseCompleted(user.uid, selectedPlan);
              purchaseCompletedRef.current = true;
              const returnUrl = location.state?.returnUrl || '/templates';
              navigate(returnUrl, {
                state: { message: `Successfully upgraded to ${plan.name} plan!`, showSuccess: true }
              });
              return;
            }
          } catch (checkErr) {
            console.warn('⚠️ Order status check failed:', checkErr);
          }
        }
        customToast.info('Payment was cancelled.');
      } else {
        // Payment.failed fired — but money might still have been captured (UPI delay)
        // Check with server before declaring failure
        if (error.orderId) {
          console.log('🔍 Payment error — verifying order status server-side...');
          customToast.info('Verifying payment status... please wait.');
          try {
            // Wait a short delay to allow Razorpay to process
            await new Promise(resolve => setTimeout(resolve, 3000));
            const statusResult = await checkOrderStatus({
              orderId: error.orderId,
              userId: user.uid,
              planType: selectedPlan
            });
            if (statusResult.success && statusResult.status === 'paid') {
              console.log('✅ Payment was actually captured despite frontend error! Recovering...');
              customToast.success(`🎉 Payment successful! ${plan.name} plan activated.`);
              await emailService.markPurchaseCompleted(user.uid, selectedPlan);
              purchaseCompletedRef.current = true;
              const returnUrl = location.state?.returnUrl || '/templates';
              navigate(returnUrl, {
                state: { message: `Successfully upgraded to ${plan.name} plan!`, showSuccess: true }
              });
              return;
            }
          } catch (checkErr) {
            console.warn('⚠️ Order status check failed:', checkErr);
          }
        }
        customToast.error(`Payment failed: ${error.message}`);
        // Notify backend to send payment declined email
        const currentUser = auth.currentUser;
        if (currentUser) {
          emailService.notifyPaymentFailed(currentUser.uid, currentUser.email, currentUser.displayName || '');
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        {/* Header */}
        <div className="pricing-header">
          <h1 className="pricing-title">Choose Your Plan</h1>
          <p className="pricing-subtitle">Unlock AI-powered career tools</p>
        </div>

        {/* Pricing Cards */}
        {(() => {
          const currentPlanType = (userData?.planType || 'free').toLowerCase();
          const hasActivePlan = userData?.planExpiryDate 
            ? (userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate)) > new Date()
            : false;
          const isActivePaidPlan = hasActivePlan && currentPlanType !== 'free';
          return (
        <div className={`pricing-cards${isActivePaidPlan ? ' has-active-plan' : ''}`}>
          {plans.map((plan) => {
            const currentPlan = currentPlanType;
            const isCurrentPlanActive = hasActivePlan;
            const isCurrentPlan = isCurrentPlanActive && currentPlan === plan.id;
            const isDowngrade = isCurrentPlanActive && currentPlan !== 'free' && (PLAN_HIERARCHY[plan.id] || 0) < (PLAN_HIERARCHY[currentPlan] || 0);
            // Show 'popular' (recommended) badge only for free/inactive users
            const showPopular = plan.popular && (!isCurrentPlanActive || currentPlan === 'free');
            // Only free/non-logged users see the bigger popular card; plan users see all cards equal
            const isProminent = showPopular;

            return (
            <div
              key={plan.id}
              className={`pricing-card ${isProminent ? 'popular' : ''} ${
                selectedPlan === plan.id ? 'selected' : ''
              } ${isCurrentPlan ? 'current-plan' : ''} ${isDowngrade ? 'downgrade' : ''}`}
              onClick={() => {
                if (isDowngrade) {
                  customToast.error(`You can't downgrade from ${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}. Your plan is still active.`);
                  return;
                }
                if (isCurrentPlan) {
                  customToast.info(`You already have an active ${plan.name} plan.`);
                  return;
                }
                handlePlanSelect(plan.id);
              }}
            >
              {/* Current Plan Badge */}
              {isCurrentPlan && (
                <div className="current-plan-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#10b981"/>
                    <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>CURRENT PLAN</span>
                </div>
              )}

              {/* Popular Badge - only for free/inactive users */}
              {showPopular && (
                <div className="popular-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3 6 6 1-4.5 4.5L18 20l-6-3-6 3 1.5-6.5L3 9l6-1z" fill="#000"/>
                  </svg>
                  <span>RECOMMENDED</span>
                </div>
              )}

              {/* Selected Badge */}
              {selectedPlan === plan.id && (
                <div className="selected-badge">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#10b981" stroke="#fff" strokeWidth="2"/>
                    <path d="M7 12l3 3 7-7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}

              {/* Plan Icon */}
              <div className="plan-icon-wrapper">
                {plan.id === 'silver' && (
                  <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                    <defs>
                      <linearGradient id="silverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#C0C0C0"/>
                        <stop offset="50%" stopColor="#E8E8E8"/>
                        <stop offset="100%" stopColor="#A8A8A8"/>
                      </linearGradient>
                    </defs>
                    <circle cx="32" cy="32" r="28" fill="url(#silverGrad)" stroke="#A0A0A0" strokeWidth="2"/>
                    <circle cx="32" cy="32" r="20" fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.6"/>
                    <circle cx="32" cy="32" r="12" fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.4"/>
                  </svg>
                )}
                {plan.id === 'gold' && (
                  <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                    <defs>
                      <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFD700"/>
                        <stop offset="50%" stopColor="#FFC700"/>
                        <stop offset="100%" stopColor="#FFB700"/>
                      </linearGradient>
                    </defs>
                    <circle cx="32" cy="32" r="28" fill="url(#goldGrad)" stroke="#E0A800" strokeWidth="2"/>
                    <path d="M32 12 L38 28 L54 28 L42 38 L46 54 L32 44 L18 54 L22 38 L10 28 L26 28 Z" 
                          fill="#FFFFFF" opacity="0.5"/>
                    <circle cx="32" cy="32" r="8" fill="#FFFFFF" opacity="0.4"/>
                  </svg>
                )}
                {plan.id === 'platinum' && (
                  <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                    <defs>
                      <linearGradient id="platGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#B8B8D8"/>
                        <stop offset="50%" stopColor="#E0E0F0"/>
                        <stop offset="100%" stopColor="#A0A0C8"/>
                      </linearGradient>
                    </defs>
                    <circle cx="32" cy="32" r="28" fill="url(#platGrad)" stroke="#9090C0" strokeWidth="2"/>
                    <path d="M32 8 L48 20 L48 44 L32 56 L16 44 L16 20 Z" 
                          fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.6"/>
                    <path d="M32 16 L42 26 L42 38 L32 48 L22 38 L22 26 Z" 
                          fill="#FFFFFF" opacity="0.3"/>
                    <circle cx="32" cy="32" r="6" fill="#FFFFFF" opacity="0.6"/>
                  </svg>
                )}
              </div>

              {/* Plan Header */}
              <div className="plan-header">
                <h2 className="plan-name">{plan.name}</h2>
                <div className="plan-pricing">
                  {loadingPlans ? (
                    <div className="price-display">
                      <div className="price-loading">
                        <div className="loading-shimmer"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="price-display">
                      {plan.originalPrice && plan.originalPrice !== plan.price && (
                        <div className="original-price">
                          <span className="currency">₹</span>
                          <span className="price-amount">{plan.originalPrice}</span>
                        </div>
                      )}
                      <div className="current-price">
                        <span className="currency">₹</span>
                        <span className="price-amount">{plan.price}</span>
                      </div>
                    </div>
                  )}
                  <p className="price-duration">{plan.duration}</p>
                </div>
              </div>

              {/* Plan Features */}
              <div className="plan-features">
                <ul className="features-list">
                  {plan.features.map((feature, index) => (
                    <li key={index} className={`feature-item ${!feature.included ? 'excluded' : ''}`}>
                      <span className="feature-status">
                        {feature.included ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="#10b981"/>
                            <path d="M7 12l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="#cbd5e1"/>
                            <path d="M8 12h8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        )}
                      </span>
                      <span className="feature-text">{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
          })}
        </div>
          );
        })()}

        {/* Footer */}
        <div className="pricing-footer">
          {/* Promo Code Section */}
          <div className="promo-code-section">
            <div className="promo-code-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" fill="currentColor"/>
              </svg>
              <span>Have a Promo Code?</span>
            </div>
            <div className="promo-code-input-wrapper">
              <input
                type="text"
                className="promo-code-input"
                placeholder="Enter Promo Code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                disabled={!!appliedPromo || promoApplying}
                onKeyDown={(e) => e.key === 'Enter' && !appliedPromo && handleApplyPromo()}
              />
              {appliedPromo ? (
                <button className="promo-remove-btn" onClick={handleRemovePromo}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  Remove
                </button>
              ) : (
                <button
                  className={`promo-apply-btn ${promoApplying ? 'applying' : ''}`}
                  onClick={handleApplyPromo}
                  disabled={promoApplying}
                >
                  {promoApplying ? (
                    <>
                      <div className="promo-spinner"></div>
                      Checking...
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
              )}
            </div>
            {appliedPromo && selectedPlan && (() => {
              const priceInfo = getDiscountedPrice();
              if (!priceInfo) return null;
              return (
                <div className="promo-success-banner">
                  <div className="promo-success-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#10b981"/>
                      <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="promo-success-details">
                    <span className="promo-code-tag">{appliedPromo.code}</span>
                    <span className="promo-discount-text">
                      {appliedPromo.discountType === 'flat'
                        ? `₹${appliedPromo.discountOffer} OFF applied`
                        : `${appliedPromo.discountOffer}% OFF applied`
                      }
                    </span>
                  </div>
                  <div className="promo-price-breakdown">
                    <span className="promo-original-price">₹{priceInfo.originalPrice}</span>
                    <span className="promo-final-price">₹{priceInfo.finalPrice}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          <button
            className={`proceed-payment-btn ${!selectedPlan || isProcessing ? 'disabled' : ''}`}
            onClick={handleProceedToPayment}
            disabled={!selectedPlan || isProcessing}
          >
            {isProcessing ? (
              <>
                <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                <span>Processing Payment...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" fill="currentColor" opacity="0.9"/>
                  <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Proceed to Secure Payment</span>
              </>
            )}
          </button>
        </div>

        {/* Payment Information Section */}
        <div className="payment-info-section">
          <div className="payment-info-container">
            <div className="payment-info-header">
              <div className="payment-icon">
                <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
                  <defs>
                    <linearGradient id="cardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95"/>
                      <stop offset="100%" stopColor="#f0f0f0" stopOpacity="0.95"/>
                    </linearGradient>
                    <linearGradient id="chipGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ffd700"/>
                      <stop offset="100%" stopColor="#ffed4e"/>
                    </linearGradient>
                    <linearGradient id="shineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0"/>
                      <stop offset="50%" stopColor="#ffffff" stopOpacity="0.3"/>
                      <stop offset="100%" stopColor="#ffffff" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  
                  {/* Card Base */}
                  <rect x="4" y="14" width="56" height="36" rx="4" fill="url(#cardGradient)" stroke="#d0d0d0" strokeWidth="1.5"/>
                  
                  {/* Magnetic Strip */}
                  <rect x="4" y="20" width="56" height="6" fill="#2d3748"/>
                  
                  {/* EMV Chip */}
                  <g transform="translate(12, 30)">
                    <rect x="0" y="0" width="12" height="10" rx="1.5" fill="url(#chipGradient)" stroke="#daa520" strokeWidth="0.5"/>
                    <line x1="3" y1="0" x2="3" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                    <line x1="6" y1="0" x2="6" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                    <line x1="9" y1="0" x2="9" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                    <line x1="0" y1="2.5" x2="12" y2="2.5" stroke="#daa520" strokeWidth="0.3"/>
                    <line x1="0" y1="5" x2="12" y2="5" stroke="#daa520" strokeWidth="0.3"/>
                    <line x1="0" y1="7.5" x2="12" y2="7.5" stroke="#daa520" strokeWidth="0.3"/>
                  </g>
                  
                  {/* Card Numbers (dots) */}
                  <circle cx="30" cy="35" r="1.2" fill="#4a5568"/>
                  <circle cx="34" cy="35" r="1.2" fill="#4a5568"/>
                  <circle cx="38" cy="35" r="1.2" fill="#4a5568"/>
                  <circle cx="42" cy="35" r="1.2" fill="#4a5568"/>
                  
                  <circle cx="30" cy="42" r="1.2" fill="#4a5568"/>
                  <circle cx="34" cy="42" r="1.2" fill="#4a5568"/>
                  <circle cx="38" cy="42" r="1.2" fill="#4a5568"/>
                  <circle cx="42" cy="42" r="1.2" fill="#4a5568"/>
                  
                  {/* Diagonal Shine Effect */}
                  <rect x="20" y="10" width="20" height="50" rx="2" fill="url(#shineGradient)" transform="rotate(-15 32 32)" opacity="0.6"/>
                </svg>
              </div>
              <h2>💳 Payment Information</h2>
              <p className="payment-subtitle">We provide secure and convenient payment options for users in India.</p>
            </div>

            <div className="payment-info-grid">
              {/* Accepted Payment Methods */}
              <div className="payment-info-card">
                <div className="card-icon">
                  <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
                    <defs>
                      <linearGradient id="cardGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.9"/>
                        <stop offset="100%" stopColor="#059669" stopOpacity="0.9"/>
                      </linearGradient>
                      <linearGradient id="chipGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ffd700"/>
                        <stop offset="100%" stopColor="#ffed4e"/>
                      </linearGradient>
                    </defs>
                    
                    {/* Card Base */}
                    <rect x="4" y="14" width="56" height="36" rx="4" fill="url(#cardGradient2)" stroke="#ffffff" strokeWidth="1.5" opacity="0.95"/>
                    
                    {/* Magnetic Strip */}
                    <rect x="4" y="20" width="56" height="6" fill="#1f2937" opacity="0.8"/>
                    
                    {/* EMV Chip */}
                    <g transform="translate(12, 30)">
                      <rect x="0" y="0" width="12" height="10" rx="1.5" fill="url(#chipGradient2)" stroke="#daa520" strokeWidth="0.5"/>
                      <line x1="3" y1="0" x2="3" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                      <line x1="6" y1="0" x2="6" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                      <line x1="9" y1="0" x2="9" y2="10" stroke="#daa520" strokeWidth="0.3"/>
                    </g>
                    
                    {/* Card Numbers */}
                    <circle cx="30" cy="35" r="1" fill="#ffffff" opacity="0.8"/>
                    <circle cx="34" cy="35" r="1" fill="#ffffff" opacity="0.8"/>
                    <circle cx="38" cy="35" r="1" fill="#ffffff" opacity="0.8"/>
                    <circle cx="42" cy="35" r="1" fill="#ffffff" opacity="0.8"/>
                  </svg>
                </div>
                <h3>Accepted Payment Methods</h3>
                <ul className="payment-methods-list">
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span><strong>UPI</strong> (Google Pay, PhonePe, Paytm, BHIM, etc.)</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span><strong>Debit Cards</strong> (Visa, Mastercard, RuPay)</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span><strong>Credit Cards</strong> (Visa, Mastercard, RuPay)</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span><strong>Net Banking</strong> (All major Indian banks)</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span><strong>Wallets</strong> (as supported by Razorpay)</span>
                  </li>
                </ul>
              </div>

              {/* Payment Confirmation */}
              <div className="payment-info-card">
                <div className="card-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <h3>Payment Confirmation</h3>
                <ul className="payment-methods-list">
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>A payment confirmation email will be sent to your registered email account after successful payment.</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>The email will include your payment reference ID and plan details.</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>If you can't find the email, please check your Spam/Promotions folder.</span>
                  </li>
                </ul>
              </div>

              {/* Pricing & Taxes */}
              <div className="payment-info-card">
                <div className="card-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <h3>Pricing & Taxes</h3>
                <ul className="payment-methods-list">
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>All prices are displayed in Indian Rupees (₹).</span>
                  </li>
                  <li>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>Prices shown are final. GST is not applicable.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Secure Payments Banner */}
            <div className="secure-payment-banner">
              <div className="secure-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div className="secure-content">
                <h3>🔒 Secure Payments</h3>
                <p>Payments are securely processed through Razorpay. We do not store your card, UPI, or banking details on our servers.</p>
              </div>
            </div>

            {/* Need Help */}
            <div className="need-help-section">
              <h3>Need Help?</h3>
              <p>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '8px' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Contact us through <a href="mailto:support@resumeglobe.com">support@resumeglobe.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
